import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { ChevronLeft, Printer, GitBranch, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, clientName } from "../lib/format";
import { QUOTATION_STATUS_LABEL } from "../lib/constants";

export default function QuotationDetail() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const nav = useNavigate();

  const load = () => api.get(`/quotations/${id}`).then(({ data }) => setQ(data));
  useEffect(() => { load(); }, [id]);
  if (!q) return <div className="text-sm text-[#8C9096]">Memuat penawaran...</div>;

  const revise = async () => {
    const { data } = await api.post(`/quotations/${q.id}/revise`);
    toast.success("Revisi dibuat: " + data.quotation_code);
    nav(`/quotations/${data.id}`);
  };
  const accept = async () => { await api.post(`/quotations/${q.id}/accept`); toast.success("Penawaran diterima"); load(); };
  const convert = async () => {
    const { data } = await api.post(`/quotations/${q.id}/convert-to-booking`);
    toast.success("Pemesanan dibuat: " + data.booking_code);
    nav(`/bookings/${data.id}`);
  };

  return (
    <div className="space-y-6" data-testid="quotation-detail">
      <div className="flex items-center gap-3">
        <Link to="/quotations" className="text-sm text-[#5C6066] hover:text-brand inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Kembali</Link>
      </div>

      <Card className="p-6 border border-[#E5E0D8] bg-white shadow-none">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-brand">{q.quotation_code}</span>
              <StatusBadge status={q.status} label={QUOTATION_STATUS_LABEL[q.status]} />
              {q.version > 1 && <span className="text-xs text-[#8C9096]">Versi {q.version}</span>}
            </div>
            <h1 className="font-display text-2xl font-bold mt-2">{q.event_name}</h1>
            <div className="text-sm text-[#5C6066]">Klien: {clientName(q.client)}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.open(`/print/quotation/${q.id}`, "_blank")} data-testid="btn-print-quotation">
              <Printer className="w-4 h-4 mr-1.5" /> Cetak
            </Button>
            {q.status === "Draft" || q.status === "Sent" ? (
              <>
                <Button variant="outline" size="sm" onClick={revise} data-testid="btn-revise-quotation">
                  <GitBranch className="w-4 h-4 mr-1.5" /> Revisi
                </Button>
                <Button variant="outline" size="sm" onClick={accept} data-testid="btn-accept-quotation">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Terima
                </Button>
              </>
            ) : null}
            {q.status === "Accepted" && !q.converted_booking_id && (
              <Button className="bg-brand text-white" size="sm" onClick={convert} data-testid="btn-convert-quotation">
                <ArrowRight className="w-4 h-4 mr-1.5" /> Konversi ke Pemesanan
              </Button>
            )}
            {q.converted_booking_id && (
              <Link to={`/bookings/${q.converted_booking_id}`} className="text-xs text-brand hover:underline">→ Buka Pemesanan</Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#F0EBE0] text-sm">
          <div><div className="text-xs text-[#8C9096]">Tanggal Acara</div><div>{formatDate(q.event_date)}</div></div>
          <div><div className="text-xs text-[#8C9096]">Venue</div><div>{q.venue || "-"}</div></div>
          <div><div className="text-xs text-[#8C9096]">Lokasi</div><div>{q.location || "-"}</div></div>
          <div><div className="text-xs text-[#8C9096]">Berlaku Sampai</div><div>{formatDate(q.valid_until)}</div></div>
        </div>
      </Card>

      <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E0D8]"><h2 className="font-display font-semibold">Item Penawaran</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                <th className="text-left px-5 py-3">Deskripsi</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Harga</th>
                <th className="text-right px-4 py-3">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {q.lines.map((l, i) => (
                <tr key={i} className="border-t border-[#F0EBE0]">
                  <td className="px-5 py-3">{l.description}</td>
                  <td className="px-4 py-3 text-right">{l.quantity}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatIDR(l.unit_price)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatIDR(l.quantity * l.unit_price)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#F0EBE0]">
                <td colSpan={3} className="px-5 py-2 text-right text-[#5C6066]">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{formatIDR(q.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={3} className="px-5 py-2 text-right text-[#5C6066]">Diskon</td>
                <td className="px-4 py-2 text-right font-mono">- {formatIDR(q.discount)}</td>
              </tr>
              <tr className="border-t border-[#F0EBE0]">
                <td colSpan={3} className="px-5 py-3 text-right font-semibold">Total</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatIDR(q.final_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {(q.versions || []).length > 1 && (
        <Card className="border border-[#E5E0D8] bg-white shadow-none">
          <div className="px-5 py-4 border-b border-[#E5E0D8]"><h2 className="font-display font-semibold">Riwayat Versi</h2></div>
          <div className="divide-y divide-[#F0EBE0]">
            {q.versions.map((v) => (
              <Link key={v.id} to={`/quotations/${v.id}`} className="px-5 py-3 flex justify-between hover:bg-[#F7F4EE]">
                <div>
                  <div className="font-mono text-xs text-brand">{v.quotation_code}</div>
                  <div className="text-xs text-[#8C9096]">Versi {v.version}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm">{formatIDR(v.final_total)}</div>
                  <StatusBadge status={v.status} label={QUOTATION_STATUS_LABEL[v.status]} />
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
